import { useMemo } from 'react'

import { useNavigationParams } from '../../../system/useNavigationParams'
import { useOperationsContext } from '../../../workspace/operationsContext'
import { useAllLayersOnServer } from './useAllLayersOnServer'
import { useConfiguredLayers } from './useConfiguredLayers'

/**
 * Sorts the set of layers returned by useAllLayersOnServer based on whether they are configured or not.
 * Requires access to an operations context to get the configured layers.
 */
export const useSortedLayersOnBlock = (block: string) => {
  const { server } = useNavigationParams()
  const { registeredTenantCreateOperations } = useOperationsContext()
  const allLayers = useAllLayersOnServer({
    server,
    allTenants: registeredTenantCreateOperations,
  })
  const configuredLayers = useConfiguredLayers()
  return useMemo(
    () =>
      allLayers.get(block)?.toSorted((a, b) => {
        const isAConfigured = configuredLayers.some((layer) => layer.equals(a))
        const isBConfigured = configuredLayers.some((layer) => layer.equals(b))

        if (isAConfigured !== isBConfigured) {
          // Configured layers come first
          return isAConfigured ? -1 : 1
        }

        // If both are configured or both are not, compare equally
        if (a.tenantId && b.tenantId) {
          return a.tenantId.localeCompare(b.tenantId, undefined, {
            sensitivity: 'base',
          })
        }
        if (a.tenantId) {
          return -1
        }
        if (b.tenantId) {
          return 1
        }
        return (a.layerId ?? '').localeCompare(b.layerId ?? '', undefined, {
          sensitivity: 'base',
        })
      }),
    [allLayers, configuredLayers, block],
  )
}
